export interface SplitResult {
    primary: string;
    quoted: string;
    hasQuoted: boolean;
}

const QUOTED_CONTAINER_SELECTORS = [
    "blockquote",
    ".gmail_quote",
    "div.gmail_quote",
    ".moz-cite-prefix",
    "#divRplyFwdMsg",
    "#appendonsend",
    ".OutlookMessageHeader",
    "#OLK_SRC_BODY_SECTION",
    ".yahoo_quoted",
    "#yahoo_quoted",
];

const ATTRIBUTION_PATTERNS: RegExp[] = [
    /^\s*on\s.+\bwrote:\s*$/i,
    /^\s*on\s.+\bat\s.+\bwrote:\s*$/i,
    /^\s*-{2,}\s*original message\s*-{2,}\s*$/i,
    /^\s*from:\s/i,
    /^\s*le\s.+\ba\s+écrit\s*:\s*$/i,
];

function nodeMatchesAny(node: Element, selectors: string[]): boolean {
    for (const selector of selectors) {
        try {
            if (node.matches(selector)) {
                return true;
            }
        } catch {
            continue;
        }
    }
    return false;
}

function isAttributionLine(text: string): boolean {
    const trimmed = text.trim();
    if (!trimmed) {
        return false;
    }
    return ATTRIBUTION_PATTERNS.some(pattern => pattern.test(trimmed));
}

function findQuoteStartNode(root: HTMLElement): ChildNode | null {
    let containerCandidate: ChildNode | null = null;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
    let current = walker.nextNode() as Element | null;
    while (current) {
        if (nodeMatchesAny(current, QUOTED_CONTAINER_SELECTORS)) {
            containerCandidate = current as unknown as ChildNode;
            break;
        }
        current = walker.nextNode() as Element | null;
    }

    if (containerCandidate) {
        const topLevel = ascendToTopLevel(containerCandidate, root);
        const previous = findPrecedingAttribution(topLevel);
        return previous ?? topLevel;
    }

    let attributionMatch: ChildNode | null = null;
    for (let i = 0; i < root.childNodes.length; i++) {
        const child = root.childNodes[i];
        if (child.nodeType === Node.ELEMENT_NODE || child.nodeType === Node.TEXT_NODE) {
            const text = (child.textContent || "").trim();
            if (isAttributionLine(text)) {
                attributionMatch = child;
                break;
            }
        }
    }
    return attributionMatch;
}

function ascendToTopLevel(node: ChildNode, root: HTMLElement): ChildNode {
    let current = node;
    while (current.parentNode && current.parentNode !== root) {
        const parent = current.parentNode;
        const childParent = root.contains(parent) ? (parent as Node as ChildNode) : null;
        if (!childParent) {
            return current;
        }
        current = childParent;
    }
    return current;
}

function findPrecedingAttribution(node: ChildNode): ChildNode | null {
    let cursor = node.previousSibling;
    while (cursor) {
        if (cursor.nodeType === Node.TEXT_NODE) {
            if ((cursor.textContent || "").trim() === "") {
                cursor = cursor.previousSibling;
                continue;
            }
            if (isAttributionLine(cursor.textContent || "")) {
                return cursor;
            }
            return null;
        }
        if (cursor.nodeType === Node.ELEMENT_NODE) {
            const el = cursor as Element;
            if (el.tagName === "BR") {
                cursor = cursor.previousSibling;
                continue;
            }
            const text = (el.textContent || "").trim();
            if (text === "") {
                cursor = cursor.previousSibling;
                continue;
            }
            if (isAttributionLine(text)) {
                return cursor;
            }
            return null;
        }
        cursor = cursor.previousSibling;
    }
    return null;
}

export function splitQuotedHtml(html: string): SplitResult {
    if (!html || !html.trim()) {
        return { primary: html ?? "", quoted: "", hasQuoted: false };
    }

    if (typeof document === "undefined" || typeof DOMParser === "undefined") {
        return { primary: html, quoted: "", hasQuoted: false };
    }

    const parser = new DOMParser();
    const parsed = parser.parseFromString(`<body>${html}</body>`, "text/html");
    const body = parsed.body;
    if (!body) {
        return { primary: html, quoted: "", hasQuoted: false };
    }

    const quoteStart = findQuoteStartNode(body);
    if (!quoteStart) {
        return { primary: html, quoted: "", hasQuoted: false };
    }

    const primaryDoc = document.implementation.createHTMLDocument("");
    const quotedDoc = document.implementation.createHTMLDocument("");

    let reachedQuote = false;
    const children = Array.from(body.childNodes);
    for (const child of children) {
        if (child === quoteStart) {
            reachedQuote = true;
        }
        if (!reachedQuote) {
            primaryDoc.body.appendChild(primaryDoc.importNode(child, true));
        } else {
            quotedDoc.body.appendChild(quotedDoc.importNode(child, true));
        }
    }

    return {
        primary: primaryDoc.body.innerHTML,
        quoted: quotedDoc.body.innerHTML,
        hasQuoted: true,
    };
}

const PLAIN_ATTRIBUTION_PATTERNS: RegExp[] = [
    /^\s*on\s.+\bwrote:\s*$/i,
    /^\s*-{2,}\s*original message\s*-{2,}\s*$/i,
    /^\s*-{2,}\s*forwarded message\s*-{2,}\s*$/i,
];

export function splitQuotedPlainText(text: string): SplitResult {
    if (!text) {
        return { primary: "", quoted: "", hasQuoted: false };
    }
    const lines = text.split(/\r?\n/);
    let splitIndex = -1;

    for (let i = 0; i < lines.length; i++) {
        if (PLAIN_ATTRIBUTION_PATTERNS.some(pattern => pattern.test(lines[i]))) {
            splitIndex = i;
            break;
        }
    }

    if (splitIndex === -1) {
        const quoteRunStart = findFirstQuoteRun(lines);
        if (quoteRunStart !== -1) {
            splitIndex = quoteRunStart;
        }
    }

    if (splitIndex === -1) {
        return { primary: text, quoted: "", hasQuoted: false };
    }

    const primaryLines = lines.slice(0, splitIndex);
    while (
        primaryLines.length > 0 &&
        primaryLines[primaryLines.length - 1].trim() === ""
    ) {
        primaryLines.pop();
    }
    const quotedLines = lines.slice(splitIndex);

    return {
        primary: primaryLines.join("\n"),
        quoted: quotedLines.join("\n"),
        hasQuoted: true,
    };
}

function findFirstQuoteRun(lines: string[]): number {
    for (let i = 0; i < lines.length; i++) {
        if (/^\s*>/.test(lines[i])) {
            const next = lines[i + 1];
            if (next !== undefined && /^\s*>/.test(next)) {
                return i;
            }
        }
    }
    return -1;
}
